import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";
import { buildSetupSections, type SetupSectionData } from "../utils/setupView";
import SetupModal from "./SetupModal";
import type { TireData } from "../types";

interface SetupValuesResponse {
  runId: string;
  setupName: string;
  raw: Record<string, unknown> | null;
}

interface Props {
  runId: string;
  teamId: string | null;
  trackWeekId: string | null;
  setupName: string | null;
  hasSto: boolean;
  stoFilename: string | null;
  isOwnRun: boolean;
  tireData: TireData | null;
  unit?: string;
  stoBusy: "download" | "upload" | null;
  stoStatus: string | null;
  onDownload: () => void;
  onAttach: (file: File) => void;
}

/**
 * Run-page launcher for the setup viewer. Renders a thin row (setup name +
 * value count + a "View setup" trigger) and opens the garage-faithful
 * SetupModal. Fetches the run's raw CarSetup once and hands the grouped
 * sections to the modal (no second fetch).
 */
export default function SetupValuesSection({
  runId,
  teamId,
  trackWeekId,
  setupName,
  hasSto,
  stoFilename,
  isOwnRun,
  tireData,
  unit,
  stoBusy,
  stoStatus,
  onDownload,
  onAttach,
}: Props) {
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<SetupValuesResponse>(`/api/runs/${runId}/setup`)
      .then((d) => { if (!cancelled) setRaw(d.raw); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load setup values"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId]);

  const sections = useMemo<SetupSectionData[]>(() => buildSetupSections(raw), [raw]);
  const totalParams = useMemo(
    () => sections.reduce((n, s) => {
      const pairItems = s.pairs.reduce((m, p) => m + p.left.items.length + p.right.items.length, 0);
      return n + pairItems + s.shared.length;
    }, 0),
    [sections],
  );

  const compareTo =
    teamId
      ? `/teams/${teamId}/setups/compare?run_a=${runId}${trackWeekId ? `&track_week_id=${trackWeekId}` : ""}`
      : null;
  const hasValues = sections.length > 0;

  return (
    <section className="section-shell">
      <div className="section-header">
        <div>
          <div className="section-kicker">Setup</div>
          <div className="section-heading">Every value from this setup.</div>
        </div>
      </div>

      <div className="card" style={st.launcher}>
        <div style={st.info}>
          <span style={st.name}>{setupName || stoFilename || "Setup values"}</span>
          <span style={st.sub}>
            {loading ? "Loading setup…" : error ? error : hasValues ? `${totalParams} values · ${sections.length} sections` : "No setup captured for this run."}
          </span>
        </div>
        <div style={st.actions}>
          {hasValues && compareTo && (
            <Link to={compareTo} className="btn btn-ghost" style={st.btn}>Compare</Link>
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={st.btn}
            onClick={() => setOpen(true)}
            disabled={!hasValues}
          >
            View setup
          </button>
        </div>
      </div>

      {open && (
        <SetupModal
          sections={sections}
          setupName={setupName}
          tireData={tireData}
          unit={unit}
          hasSto={hasSto}
          stoFilename={stoFilename}
          isOwnRun={isOwnRun}
          stoBusy={stoBusy}
          stoStatus={stoStatus}
          onDownload={onDownload}
          onAttach={onAttach}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
}

const st: Record<string, CSSProperties> = {
  launcher: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "0.95rem 1.15rem",
    flexWrap: "wrap",
  },
  info: { display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: 0 },
  name: {
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: "0.98rem",
    color: "var(--text-strong)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sub: { fontFamily: "var(--font-mono)", fontSize: "0.8125rem", color: "var(--text-secondary)" },
  actions: { display: "flex", alignItems: "center", gap: "0.6rem", flexShrink: 0 },
  btn: { padding: "0.4rem 0.95rem", fontSize: "0.82rem" },
};
